import { useRef, useState } from 'react';
import { PartnerBillingPackage } from 'vopay';
import { api } from '@/services/api.config';

/**
 * The billing packages a new account may be put on, for one country. `bearer` reads another
 * partner account's packages, which the partner page's Add Account creates under.
 */
export default function useFetchPartnerBillingPackages({ bearer }: { bearer?: string } = {}) {
  const [fetchingBillingPackages, setFetchingBillingPackages] = useState(false);
  const [billingPackages, setBillingPackages] = useState<PartnerBillingPackage[]>([]);

  const latestRequestId = useRef(0);

  /** `country` is alpha-2: the route filters on ADRCountries.Abbreviation, and alpha-3 matches nothing. */
  async function fetchBillingPackages(country: string) {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetchingBillingPackages(true);
      // The list is per country: the previous country's packages must not stay pickable while this
      // one loads, nor after it fails.
      setBillingPackages([]);

      const res = await api.fetchBillingPackages({ Country: country }, undefined, bearer);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const packages = Object.values(res.BillingPackages);

      // The country can change again before this answers; only the newest list may land.
      if (isLatest()) setBillingPackages(packages);

      return packages;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetchingBillingPackages(false);
    }
  }

  return { billingPackages, fetchingBillingPackages, fetchBillingPackages };
}
